import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Loader } from "@progress/kendo-react-indicators";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { ValidationErrors } from "final-form";
import { Import as ImportIcon } from "lucide-react";
import React, { PropsWithChildren, useEffect } from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { Then, Unless } from "react-if";
import useWindowSize from "@/hooks/useWindowSize";
import { DisableConfirm, Deploy, EnableConfirm } from "@/icons";
import AlertSvg from "./alert.svg?react";
import InfoAlertSvg from "./info-alert.svg?react";
import TrashSVG from "./trash.svg?react";
import WarningSVG from "./warning.svg?react";
import "./dialog.scss";

interface IProps extends PropsWithChildren {
  title: string;
  open: boolean;
  onClose: () => void;
  width?: number | string;
  height?: number | string | false;
  closeIcon?: boolean;
  hideFooter?: boolean;
  note?: string | React.ReactElement<unknown>;
  customClass?: string;
}

export const Modal: React.FC<IProps> = props => {
  const [open, setOpen] = React.useState(false);
  const { height } = useWindowSize();
  const handleClose = () => {
    setOpen(false);
    props.onClose();
  };

  useEffect(() => setOpen(props.open), [props.open]);

  if (!open) return null;

  // Dialogs expose a close affordance by default. This used to default to
  // `false`, so every caller that didn't pass the prop rendered a modal with no
  // visible way out. Callers that must block dismissal still opt out explicitly.
  const showCloseIcon = props.closeIcon ?? true;

  return (
    <Dialog
      onClose={handleClose}
      // `tlx-dialog` is always present: it carries the base dialog styling
      // (content padding, the 100px action-button floor). This used to swap in
      // `tlx-dialog-title-bar` as a REPLACEMENT class when a close icon was
      // shown, so those dialogs silently lost every base rule. Nothing in the
      // repo styles that name, so it isn't reintroduced as an extra class
      // either — the close icon needs no class hook of its own.
      className={`tlx-dialog ${props.customClass ?? ""}`.trim()}
      aria-labelledby={props.title}
      title={props.title}
      closeIcon={showCloseIcon}
      width={props.width}
      height={props.height ? props.height : undefined}
    >
      <div
        style={{
          maxHeight: props.height !== false ? height - 300 : undefined,
          overflow: "auto",
        }}
      >
        {props.children}
      </div>
      <Unless condition={props.hideFooter}>
        <Then>
          <DialogActionsBar layout="center">
            {props.note && (
              <div style={{ width: "100%", paddingLeft: 20 }}>{props.note}</div>
            )}
            <Button size="medium" onClick={handleClose}>
              Close
            </Button>
          </DialogActionsBar>
        </Then>
      </Unless>
    </Dialog>
  );
};

interface IPropsConfirm extends Omit<IProps, "open">, PropsWithChildren {
  confirmationText?: string;
  cancelText?: string;
  confirmColor?: string;
  onSuccess: () => void;
  isLoading?: boolean;
  disabled?: boolean;
  closeOnSuccess?: boolean;
  iconColor?: React.CSSProperties["color"];
  appendTo?: HTMLElement;
  type?:
    | "alert-info"
    | "alert"
    | "success"
    | "warning"
    | "info"
    | "delete"
    | "deploy"
    | "disable"
    | "import"
    | "enable";
}

export const ConfirmModal: React.FC<IPropsConfirm> = props => {
  const [open, setOpen] = React.useState(true);
  const { t } = useTranslation();
  const {
    confirmColor,
    confirmationText = t("common.yes"),
    cancelText = t("common.no"),
    type,
    isLoading = false,
    disabled = false,
    hideFooter = false,
    closeOnSuccess = true,
    customClass = "",
    iconColor = "var(--tlx-color-neutral-white-800)",
    appendTo,
  } = props;

  const handleClose = () => {
    setOpen(false);
    props.onClose();
  };
  const onSuccess = () => {
    props.onSuccess?.();
    if (closeOnSuccess) {
      handleClose();
    }
  };

  if (!open) return null;

  return (
    <Dialog
      onClose={handleClose}
      closeIcon
      aria-labelledby="customized-dialog-title"
      className={`tlx-dialog tlx-dialog-confirm ${customClass}`}
      // A non-empty title is what makes Kendo render the titlebar that hosts the
      // close button; the visible heading is `.tlx-dialog-title` in the content.
      title=" "
      width={props.width || 650}
      autoFocus
      appendTo={appendTo}
    >
      <div className={`k-dialog-content confirm-dialog-content ${type || ""}`}>
        {type === "warning" && <WarningSVG />}
        {type === "alert" && <AlertSvg color="var(--tlx-status-error-text)" />}
        {type === "delete" && <TrashSVG />}
        {type === "alert-info" && <InfoAlertSvg />}
        {type === "deploy" && <Deploy width={96} height={70} />}
        {type === "disable" && <DisableConfirm />}
        {type === "enable" && <EnableConfirm />}
        {type === "import" && (
          <ImportIcon width={96} height={70} color={iconColor} />
        )}
        <div className="tlx-dialog-title">{props.title}</div>
        {props.children}
      </div>
      <Unless condition={hideFooter}>
        <Then>
          <DialogActionsBar layout="end">
            <Button size="medium" onClick={handleClose}>
              {cancelText}
            </Button>
            <Button
              size="medium"
              themeColor="secondary"
              disabled={isLoading || disabled}
              style={
                confirmColor
                  ? { backgroundColor: confirmColor, borderColor: confirmColor }
                  : {}
              }
              onClick={onSuccess}
            >
              {confirmationText}
              {isLoading && <Loader size="small" themeColor="light" />}
            </Button>
          </DialogActionsBar>
        </Then>
      </Unless>
    </Dialog>
  );
};

interface ISaveProps extends IProps {
  onSave: (values: Record<string, unknown>) => void;
  closeText?: string;
  saveText?: string;
  isLoading?: boolean;
  initialValues?: object;
  validate?: (
    values: object,
  ) => ValidationErrors | Promise<ValidationErrors> | undefined;
  description?: string;
}

export const FormModal: React.FC<ISaveProps> = props => {
  const [open, setOpen] = React.useState(false);
  const {
    closeText = "Cancel",
    saveText = "Save",
    validate,
    initialValues,
    isLoading,
  } = props;

  const handleClose = () => {
    setOpen(false);
    props.onClose();
  };

  const onSave = React.useCallback((values: Record<string, unknown>) => {
    props.onSave(values);
    // onSave is not a dependency of the hook
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => setOpen(props.open), [props.open]);
  const CustomTitleBar = () => {
    return (
      <GridLayout style={{ marginBottom: 15 }}>
        <GridLayoutItem>
          {props.title}
          <br />
          <div style={{ fontSize: 14 }}>{props.description}</div>
        </GridLayoutItem>
      </GridLayout>
    );
  };
  if (!open) return null;
  return (
    <Dialog
      onClose={handleClose}
      className="tlx-dialog"
      aria-labelledby="customized-dialog-title"
      closeIcon
      title={<CustomTitleBar />}
    >
      <Form
        validate={validate}
        initialValues={initialValues}
        onSubmit={onSave}
        render={({ handleSubmit }) => (
          <form onSubmit={handleSubmit} noValidate style={{ width: "100%" }}>
            <p className="k-dialog-content">{props.children}</p>
            <DialogActionsBar layout="center">
              <Button
                size="medium"
                onClick={handleClose}
                themeColor="primary"
                fillMode="outline"
                disabled={isLoading}
              >
                {closeText}
              </Button>
              <Button
                size="medium"
                type="submit"
                themeColor="primary"
                fillMode="outline"
                disabled={isLoading}
              >
                {saveText}{" "}
                {isLoading && (
                  <Loader
                    size="small"
                    type="infinite-spinner"
                    themeColor="light"
                  />
                )}
              </Button>
            </DialogActionsBar>
          </form>
        )}
      />
    </Dialog>
  );
};
